import React, { useState, useMemo, useEffect } from 'react';
import {
  Shield, Eye, Lock, AlertTriangle, Filter,
  ChevronRight, CheckCircle, XCircle, AlertCircle, TrendingUp, X
} from 'lucide-react';
import {
  mockFieldPermissions,
  type FieldSecuritySummary,
  getSecurityLevel,
  getSecurityLevelColor
} from '../../services/queries/fieldPermissionQueries';
import { mockObjects } from '../../services/queries/objectQueries';
import { getMockRoles } from '../../services/queries/roleQueries';
import { DevModeBanner } from '../common/DevModeBanner';
import FieldSecurityManager from './object-manager/FieldSecurityManager';
import { useRecentItems } from '../../hooks/useRecentItems';
import {
  StandardSearchBar,
  StandardSelect,
  StandardEmptyState
} from '../standard';

// ============================================
// SECURITY ISSUE CARD COMPONENT
// ============================================

interface SecurityIssueCardProps {
  issue: {
    type: string;
    message: string;
    severity: 'high' | 'medium' | 'low';
  };
  onClick: () => void;
}

const SecurityIssueCard: React.FC<SecurityIssueCardProps> = ({ issue, onClick }) => {
  const severityConfig = {
    high: {
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
      iconColor: 'text-red-600 dark:text-red-400',
      badgeColor: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    },
    medium: {
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      badgeColor: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
    },
    low: {
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
      iconColor: 'text-blue-600 dark:text-blue-400',
      badgeColor: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    }
  };

  const config = severityConfig[issue.severity];

  return (
    <div
      className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex-shrink-0">
            <AlertCircle className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${config.badgeColor}`}>
                {issue.severity.toUpperCase()}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {issue.type}
              </span>
            </div>
            <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">
              {issue.message}
            </p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Security Health Dashboard
 *
 * Provides comprehensive overview of field-level security across all objects.
 * Shows security statistics, field restrictions, and identifies potential security issues.
 *
 * Data Source: fieldPermissionQueries.ts (React Query with mock data)
 *
 * @component
 */
export const SecurityHealthDashboard: React.FC = () => {
  const mockRoles = useMemo(() => getMockRoles(), []);
  const [searchQuery, setSearchQuery] = useState('');
  const [objectFilter, setObjectFilter] = useState<string>('all');
  const [securityFilter, setSecurityFilter] = useState<string>('all');
  const [selectedField, setSelectedField] = useState<FieldSecuritySummary | null>(null);
  const { addRecentItem } = useRecentItems();

  // Track Security Health Dashboard view in Recent Items
  useEffect(() => {
    addRecentItem({
      id: 'security-health-dashboard',
      title: 'Security Health Dashboard',
      subtitle: 'Field-Level Security Overview',
      path: '/admin?section=security-health',
      type: 'page',
      icon: 'Settings'
    });
  }, [addRecentItem]);

  // Debug: Log when selectedField changes
  useEffect(() => {
    console.log('selectedField changed:', selectedField ? selectedField.fieldLabel : 'null');
  }, [selectedField]);

  // Calculate overall security statistics
  const securityStats = useMemo(() => {
    const uniqueFields = new Set(mockFieldPermissions.map(fp => fp.fieldId)).size;
    const totalRoles = mockRoles.length;

    // Calculate security score (0-100)
    const securedFieldsCount = new Set(
      mockFieldPermissions
        .filter(fp => !fp.canView || !fp.canEdit)
        .map(fp => fp.fieldId)
    ).size;
    const securityScore = Math.round((securedFieldsCount / uniqueFields) * 100);

    // Identify security issues
    const issues: Array<{ type: string; message: string; severity: 'high' | 'medium' | 'low' }> = [];

    // Check for overly permissive fields
    const publicFields = mockFieldPermissions.filter(fp => fp.canEdit && fp.canView);
    const publicFieldsCount = new Set(publicFields.map(fp => fp.fieldId)).size;
    if (publicFieldsCount > uniqueFields * 0.5) {
      issues.push({
        type: 'permission',
        message: `${publicFieldsCount} fields have unrestricted access across all roles`,
        severity: 'medium'
      });
    }

    // Check for PII fields without restrictions
    const ssnFields = mockFieldPermissions.filter(fp =>
      fp.fieldLabel.toLowerCase().includes('ssn') && fp.canView
    );
    if (ssnFields.length > 0) {
      issues.push({
        type: 'pii',
        message: 'Sensitive PII fields (SSN) are accessible to some roles',
        severity: 'high'
      });
    }

    return {
      totalFields: uniqueFields,
      restrictedFields: securedFieldsCount,
      totalRoles,
      securityScore,
      issues
    };
  }, []);

  // Group field permissions by field and object
  const fieldSecurityData = useMemo(() => {
    const fieldMap = new Map<string, FieldSecuritySummary>();

    mockFieldPermissions.forEach(fp => {
      if (!fieldMap.has(fp.fieldId)) {
        const fieldPerms = mockFieldPermissions.filter(p => p.fieldId === fp.fieldId);
        const rolesCanView = fieldPerms.filter(p => p.canView).length;
        const rolesCanEdit = fieldPerms.filter(p => p.canEdit).length;

        fieldMap.set(fp.fieldId, {
          fieldId: fp.fieldId,
          fieldLabel: fp.fieldLabel,
          objectName: fp.objectName,
          totalRoles: mockRoles.length,
          rolesCanView,
          rolesCanEdit,
          isPublic: rolesCanView === mockRoles.length && rolesCanEdit === mockRoles.length,
          isRestricted: rolesCanView < mockRoles.length || rolesCanEdit < mockRoles.length,
          lastModified: fp.lastModified
        });
      }
    });

    return Array.from(fieldMap.values());
  }, []);

  // Filter field security data
  const filteredFields = useMemo(() => {
    return fieldSecurityData.filter(field => {
      // Search filter
      if (searchQuery && !field.fieldLabel.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Object filter
      if (objectFilter !== 'all' && field.objectName !== objectFilter) {
        return false;
      }

      // Security level filter
      if (securityFilter === 'restricted' && !field.isRestricted) {
        return false;
      }
      if (securityFilter === 'public' && !field.isPublic) {
        return false;
      }

      return true;
    });
  }, [fieldSecurityData, searchQuery, objectFilter, securityFilter]);

  // Get unique objects for filter dropdown
  const availableObjects = useMemo(() => {
    const objects = new Set(fieldSecurityData.map(f => f.objectName));
    return Array.from(objects).map(objName => {
      const obj = mockObjects.find(o => o.apiName === objName);
      return { apiName: objName, label: obj?.label || objName };
    });
  }, [fieldSecurityData]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
            <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Field-Level Security
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage field permissions and data access controls across all objects
            </p>
          </div>
        </div>
      </div>

      {/* Development Mode Banner */}
      <DevModeBanner
        dataSource="mockDataManager.ts"
        itemCount={`${securityStats.totalFields} fields, ${securityStats.totalRoles} roles`}
        customMessage={`Development Mode: Field-level security data (${securityStats.totalFields} fields, ${securityStats.totalRoles} roles) persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
        dismissible={true}
      />

      {/* Security Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Total Fields */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <TrendingUp className="w-4 h-4 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {securityStats.totalFields}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Total Fields
          </div>
        </div>

        {/* Restricted Fields */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <Lock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {securityStats.restrictedFields}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Restricted Fields
          </div>
        </div>

        {/* Active Roles */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {securityStats.totalRoles}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Active Roles
          </div>
        </div>

        {/* Security Score */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {securityStats.securityScore}%
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Security Score
          </div>
        </div>
      </div>

      {/* Security Issues Section */}
      {securityStats.issues.length > 0 && securityFilter !== 'public' && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            Security Issues ({securityStats.issues.length})
          </h3>
          <div className="space-y-3">
            {securityStats.issues.slice(0, 5).map((issue, idx) => (
              <SecurityIssueCard
                key={idx}
                issue={issue}
                onClick={() => {
                  console.log('Security Issue Card clicked:', issue.message, 'Type:', issue.type);

                  // Handle different issue types by filtering the view
                  if (issue.type === 'permission' && issue.message.includes('unrestricted access')) {
                    // Filter to show only public/unrestricted fields
                    console.log('Filtering to show unrestricted fields');
                    setSecurityFilter('all');
                    setSearchQuery('');
                    // Scroll to the field table
                    setTimeout(() => {
                      const tableElement = document.querySelector('[data-field-table]');
                      if (tableElement) {
                        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  } else if (issue.type === 'pii') {
                    // Filter to show SSN or other PII fields
                    console.log('Filtering to show PII fields');
                    setSearchQuery('ssn');
                    setSecurityFilter('all');
                    // Scroll to the field table
                    setTimeout(() => {
                      const tableElement = document.querySelector('[data-field-table]');
                      if (tableElement) {
                        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  } else {
                    // For other issue types, try to find a specific field
                    const issueField = fieldSecurityData.find(f =>
                      f.fieldLabel.toLowerCase().includes(issue.message.toLowerCase().split(' ')[0])
                    );
                    console.log('Found field:', issueField?.fieldLabel || 'Not found');
                    if (issueField) {
                      setSelectedField(issueField);
                    }
                  }
                }}
              />
            ))}
          </div>
          {securityStats.issues.length > 5 && (
            <button
              onClick={() => setSecurityFilter('restricted')}
              className="mt-3 text-sm text-purple-600 dark:text-purple-400 hover:underline font-medium"
            >
              View all {securityStats.issues.length} security issues →
            </button>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search fields..."
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <StandardSelect
              value={objectFilter}
              onChange={(e) => setObjectFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Objects' },
                ...availableObjects.map(obj => ({ value: obj.apiName, label: obj.label }))
              ]}
            />
            <StandardSelect
              value={securityFilter}
              onChange={(e) => setSecurityFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Security Levels' },
                { value: 'restricted', label: 'Restricted Only' },
                { value: 'public', label: 'Public Only' }
              ]}
            />
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {filteredFields.length} of {fieldSecurityData.length} fields
      </div>

      {/* Field Security List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-field-table>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Field
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Object
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Can View
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Can Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Security Level
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredFields.map((field) => {
                const securityLevel = getSecurityLevel(field);
                const securityColor = getSecurityLevelColor(field);

                return (
                  <tr key={field.fieldId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {field.fieldLabel}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {mockObjects.find(o => o.apiName === field.objectName)?.label || field.objectName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {field.rolesCanView === field.totalRoles ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : field.rolesCanView === 0 ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {field.rolesCanView} / {field.totalRoles} roles
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {field.rolesCanEdit === field.totalRoles ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : field.rolesCanEdit === 0 ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {field.rolesCanEdit} / {field.totalRoles} roles
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        securityColor === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        securityColor === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                        securityColor === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                        securityColor === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                        'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      }`}>
                        {securityLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => {
                          console.log('Manage button clicked for field:', field.fieldLabel);
                          setSelectedField(field);
                        }}
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm font-medium flex items-center gap-1 ml-auto focus:outline-none focus:underline"
                      >
                        Manage
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredFields.length === 0 && (
          <StandardEmptyState
            icon={<Shield className="w-16 h-16" />}
            title="No fields found"
            message={searchQuery || objectFilter !== 'all' || securityFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'No field security settings configured'}
            actionLabel={(searchQuery || objectFilter !== 'all' || securityFilter !== 'all') ? 'Clear filters' : undefined}
            onAction={(searchQuery || objectFilter !== 'all' || securityFilter !== 'all')
              ? () => {
                  setSearchQuery('');
                  setObjectFilter('all');
                  setSecurityFilter('all');
                }
              : undefined}
          />
        )}
      </div>

      {/* Field Security Detail Modal */}
      {selectedField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {selectedField.fieldLabel} - Field Security
                </h2>
                <button
                  onClick={() => {
                    console.log('Closing modal');
                    setSelectedField(null);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <FieldSecurityManager
                fieldId={selectedField.fieldId}
                fieldLabel={selectedField.fieldLabel}
                objectName={selectedField.objectName}
                mode="full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
